/**
 * One row in a data-room category: label, status icon, and upload/preview/edit
 * actions. Treats both "complete" and "uploaded" statuses as having a file.
 */
import React from 'react';
import VersionHistory from './VersionHistory';

function CheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="4" fill="var(--color-status-complete-bg)" />
      <path d="M6 12.5L10.5 17L18 8" stroke="var(--color-forest)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M12 3v10M7 8l5-5 5 5" stroke="var(--color-forest)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 17v1a2 2 0 002 2h12a2 2 0 002-2v-1" stroke="var(--color-forest)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

async function previewFile(url) {
  if (!url) return;
  try {
    if (url.startsWith('/')) {
      // Same-origin API path (local storage) — needs credentials cookie
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) return;
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } else {
      // S3 presigned URL — auth is in the query string, open directly
      window.open(url, '_blank', 'noopener');
    }
  } catch {
    // file not accessible
  }
}

const REVIEW = {
  complete:   { label: 'Completed', bg: '#dcfce7', color: '#15803d' },
  needs_work: { label: 'Needs work', bg: '#fef3c7', color: '#b45309' },
};

export default function ItemRow({ item, onUpload }) {
  const isUploaded = item.status === 'complete' || item.status === 'uploaded';
  const review = item.review && REVIEW[item.review.status] ? { ...REVIEW[item.review.status], ...item.review } : null;

  return (
    <div style={styles.row}>
      <div style={styles.main}>
        <div style={styles.labelRow} className="item-row-label-row">
          <div style={styles.iconBox}>
            {isUploaded ? <CheckIcon /> : <UploadIcon />}
          </div>
          <span style={styles.label}>
            {item.label}
            {item.optional && <span style={styles.optionalBadge}>(optional)</span>}
            {review && <span style={{ ...styles.reviewBadge, background: review.bg, color: review.color }}>{review.label}</span>}
          </span>
          {isUploaded && (
            <div style={styles.actions} className="item-row-actions">
              <button
                style={styles.previewBtn}
                onClick={() => previewFile(item.currentFile?.url)}
                disabled={!item.currentFile?.url}
              >
                Preview
              </button>
              <button style={styles.editBtn} onClick={() => onUpload(item)}>
                Edit
              </button>
            </div>
          )}
          {!isUploaded && (
            <button
              style={styles.uploadBtn}
              onClick={() => onUpload(item)}
              aria-label={`Upload ${item.label}`}
            >
              <UploadIcon />
            </button>
          )}
        </div>

        {review && review.note && (
          <p style={styles.reviewNote}>
            "{review.note}"
            {review.reviewed_by_name && <span style={styles.reviewBy}> — {review.reviewed_by_name}</span>}
          </p>
        )}

        {isUploaded && item.versions && item.versions.length > 0 && (
          <VersionHistory versions={item.versions} />
        )}
      </div>
    </div>
  );
}

const styles = {
  row: {
    borderRadius: 'var(--radius-md)',
    background: '#fff',
    border: '1.5px solid var(--color-mint-dark)',
    marginBottom: 'var(--space-3)',
    padding: 'var(--space-4)',
  },
  main: {},
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
  },
  iconBox: {
    flexShrink: 0,
  },
  label: {
    flex: 1,
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--color-forest)',
    lineHeight: 1.4,
  },
  optionalBadge: {
    marginLeft: 8,
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-forest)',
    opacity: 0.45,
    fontStyle: 'italic',
  },
  reviewBadge: {
    marginLeft: 10,
    fontSize: 12,
    fontWeight: 800,
    fontFamily: 'var(--font-body)',
    borderRadius: 'var(--radius-full)',
    padding: '2px 10px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  reviewNote: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-forest)',
    opacity: 0.8,
    fontStyle: 'italic',
    marginTop: 10,
    paddingLeft: 'calc(24px + var(--space-3))',
    lineHeight: 1.5,
  },
  reviewBy: { fontStyle: 'normal', fontWeight: 700, opacity: 0.7 },
  actions: {
    display: 'flex',
    gap: 'var(--space-2)',
    flexShrink: 0,
  },
  previewBtn: {
    padding: '5px 12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 16,
    fontWeight: 700,
    fontFamily: 'var(--font-body)',
    color: 'var(--color-forest)',
    background: 'var(--color-status-complete-bg)',
    border: '1.5px solid var(--color-forest)',
    cursor: 'pointer',
  },
  editBtn: {
    padding: '5px 12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 16,
    fontWeight: 700,
    fontFamily: 'var(--font-body)',
    color: '#fff',
    background: 'var(--color-forest)',
    border: 'none',
    cursor: 'pointer',
  },
  uploadBtn: {
    flexShrink: 0,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-mint-dark)',
    border: 'none',
    cursor: 'pointer',
  },
};
